// src/components/atoms/ProfileCard/ProfileCard.tsx
import React, { useMemo } from "react";

export type Stat = { value: number | string; label: string };
export type Relation = "self" | "family";

/** 라벨 고정 & 값만 주입 */
export const STAT_LABELS = ["연속 달성", "총 달성", "이번 달"] as const;
export const buildStats = (values: Array<number | string>): Stat[] =>
  STAT_LABELS.map((label, i) => ({ label, value: values[i] ?? 0 }));

type Props = {
  name: string;
  subinfo: string; // 예: "2016. 03. 02 | 남성"
  imgSrc: any; // PNG 경로/URL
  imgAlt?: string; // 접근성 텍스트
  stats?: Stat[];
  relation: Relation; // 본인 | 가족
  onManage?: () => void; // self일 때
  onDisconnect?: () => void; // family일 때
  onCheer?: () => void;
  onCong?: () => void; // family 하단 CTA (옵션)
  cheerLabel?: string;
  congLabel?: string; // 기본 "응원 보내기" / "축하 보내기"
  px: (n: number) => number;
  rightSlot?: React.ReactNode; // 계정관리 / 연결 끊기 버튼 분리
  fit?: "cover" | "contain"; // 아바타 이미지 핏 (아이콘 PNG면 contain 권장)
};

const ProfileCard: React.FC<Props> = ({
  name,
  subinfo,
  imgSrc,
  imgAlt,
  stats,
  relation,
  onManage,
  onDisconnect,
  onCheer,
  cheerLabel = "응원 보내기",
  onCong,
  congLabel = "축하 보내기",
  px,
  rightSlot,
  fit = "contain",
}) => {
  const isSelf = relation === "self";
  const actionHandler = isSelf ? onManage : onDisconnect;

  const columns = useMemo(
    () =>
      stats && stats.length
        ? stats
            .map((_, i) => (i < stats.length - 1 ? "1fr auto" : "1fr"))
            .join(" ")
        : "1fr",
    [stats]
  );

  return (
    <section
      style={{
        marginTop: px(16),
        background: "#FFFFFF",
        borderRadius: px(16),
        boxShadow: "0px 4px 11px rgba(0,0,0,0.04)",
        outline: `1px solid #E5E5E5`,
        outlineOffset: -1,
        padding: px(16),
      }}
    >
      {/* 헤더 */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: px(16) }}>
          {/* 아바타: 50x50 + padding:10 (피그마) */}
          <div
            style={{
              width: px(50),
              height: px(50),
              padding: px(8),
              background: "#FAFAFA",
              borderRadius: px(25),
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              outline: `${px(1)}px solid #E5E5E5`,
              outlineOffset: -1,
              overflow: "hidden",
            }}
          >
            <img
              src={imgSrc}
              alt={imgAlt ?? `${name} 아바타`}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover", // 기본 contain
                display: "block",
                borderRadius: 999,
              }}
              loading="lazy"
              decoding="async"
            />
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                color: "#404040",
                fontSize: px(18),
                fontWeight: 700,
                lineHeight: px(28) + "px",
              }}
            >
              {name}
            </div>
            <div
              style={{
                color: "#A3A3A3",
                fontSize: px(14),
                fontWeight: 500,
                lineHeight: px(20) + "px",
              }}
            >
              {subinfo}
            </div>
          </div>
        </div>

        {rightSlot ? (
          rightSlot
        ) : (
          <button
            onClick={() => actionHandler?.()}
            disabled={!actionHandler}
            aria-disabled={!actionHandler}
            style={{
              height: px(32),
              padding: `${px(8)}px ${px(12)}px`,
              background: "#F5F5F5",
              borderRadius: px(8),
              border: "none",
              display: "flex",
              alignItems: "center",
              gap: px(4),
              color: "#404040",
              fontSize: px(13),
              fontWeight: 500,
              lineHeight: px(20) + "px",
              cursor: actionHandler ? "pointer" : "not-allowed",
              opacity: actionHandler ? 1 : 0.6,
            }}
          >
            계정관리
          </button>
        )}
      </div>

      {/* 통계 */}
      <div
        style={{
          width: "100%",
          display: "grid",
          gridTemplateColumns: columns,
          alignItems: "center",
          textAlign: "center",
          gap: px(12),
          marginTop: px(16),
        }}
      >
        {stats?.map((s, i) => (
          <React.Fragment key={s.label}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  color: "#F57C00",
                  fontSize: px(18),
                  fontWeight: 700,
                  lineHeight: px(28) + "px",
                }}
              >
                {s.value}
              </div>
              <div
                style={{
                  color: "#A3A3A3",
                  fontSize: px(14),
                  fontWeight: 500,
                  lineHeight: px(20) + "px",
                }}
              >
                {s.label}
              </div>
            </div>
            {i < stats.length - 1 && (
              <div
                style={{
                  width: 1,
                  height: px(28),
                  background: "#F1F2F4",
                  justifySelf: "center",
                }}
              />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* 가족 전용 CTA */}
      {relation === "family" && (
        <div style={{ display: "flex", gap: 12 }}>
          <button
            onClick={() => onCheer?.()}
            style={{
              marginTop: px(12),
              width: "100%",
              height: px(44),
              borderRadius: px(10),
              border: "none",
              color: "#FFFFFF",
              fontSize: px(14),
              fontWeight: 700,
              cursor: "pointer",
              background: "linear-gradient(180deg, #FFA62E 0%, #F97316 100%)",
              appearance: "none",
              WebkitAppearance: "none",
            }}
          >
            {cheerLabel}
          </button>

          <button
            onClick={() => onCong?.()}
            style={{
              marginTop: px(12),
              width: "100%",
              height: px(44),
              borderRadius: px(10),
              border: "none",
              color: "#FFFFFF",
              fontSize: px(14),
              fontWeight: 700,
              cursor: "pointer",
              background: "linear-gradient(180deg, #10B7EF 0%, #10B7EF 100%)",
              appearance: "none",
              WebkitAppearance: "none",
            }}
          >
            {congLabel}
          </button>
        </div>
      )}
    </section>
  );
};

export default React.memo(ProfileCard);
